/**
 * Manifiesto frontend — Trámites Fiscales (módulo independiente).
 * Sincronizado conceptualmente con GET /api/tramites/manifest.
 */
import { TRAMITES_VALIDADOR_NSS_PATH } from "@/lib/checkIdLabels";
import { FISCAL_DOCS_OWNER } from "@/modules/sat/fiscalDocsOwnership";
import { DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH } from "@/modules/datosMaestros/datosMaestrosHub";

export const MODULO_TRAMITES_ID = "tramites_fiscales";

export const MODULO_TRAMITES_CARD = {
  id: MODULO_TRAMITES_ID,
  title: "Trámites Fiscales",
  description: "Validación de contribuyentes, altas SAT/IMSS, consulta NSS y descargas fiscales e institucionales por cliente.",
  bullets: [
    "Inscripción de trabajadores en portal SAT",
    "Validador NSS y movimientos IMSS (IDSE)",
    "Descargas IMSS e INFONAVIT por periodo",
  ],
  entryPath: "/tramites/inicio",
  path: "/tramites-fiscales",
};

export const TRAMITES_CONTEXT_LABEL = "Trámites Fiscales";

/** Pestañas de la pantalla principal (/tramites-fiscales?tab=…) */
export const TRAMITES_TAB_IDS = {
  SAT: "sat",
  VALIDACION_RFC: "validacion-rfc",
  INSCRIPCION_SAT: "inscripcion-sat",
  VALIDADOR_NSS: "validador-nss",
  INSCRIPCION_IMSS: "inscripcion-imss",
  /** Tab unificado IMSS + INFONAVIT (descargas institucionales). */
  DESCARGAS_IMSS_INFONAVIT: "descargas-imss-infonavit",
  /** @deprecated Alias legacy ?tab=imss → redirige al tab unificado. */
  IMSS: "imss",
  /** @deprecated Alias legacy ?tab=infonavit → redirige al tab unificado. */
  INFONAVIT: "infonavit",
  MATERIALIDAD: "materialidad",
};

/** Query ?tab= legacy → id canónico del manifiesto. */
export const TRAMITES_TAB_LEGACY_ALIASES = {
  [TRAMITES_TAB_IDS.IMSS]: TRAMITES_TAB_IDS.DESCARGAS_IMSS_INFONAVIT,
  [TRAMITES_TAB_IDS.INFONAVIT]: TRAMITES_TAB_IDS.DESCARGAS_IMSS_INFONAVIT,
};

export function normalizeTramitesTabId(tabId) {
  const raw = String(tabId || "").toLowerCase();
  return TRAMITES_TAB_LEGACY_ALIASES[raw] || raw;
}

export const TRAMITES_TAB_CONFIG = [
  {
    id: TRAMITES_TAB_IDS.MATERIALIDAD,
    label: "Materialidad",
    title: "Expedientes de operaciones",
    description:
      "Soporte documental: faltantes, detección CFDI, riesgos EFOS/opinión y exportación PDF.",
    iconKey: "folder",
    navSection: "Documentación",
    /** Movido a módulo propio BNT Materialidad; redirect desde /tramites/materialidad. */
    hideFromHub: true,
    embedded: TRAMITES_TAB_IDS.MATERIALIDAD,
  },
  {
    id: TRAMITES_TAB_IDS.SAT,
    label: "SAT",
    title: "Documentos fiscales SAT",
    description:
      "Acceso operativo desde Trámites. CSF y opinión de cumplimiento se gestionan con el módulo Visor SAT (panel compartido; no duplica lógica).",
    iconKey: "landmark",
    navSection: "SAT",
    /** Oculto en hub/sidebar: pantalla canónica en Datos Maestros → /documentos-fiscales-sat */
    hideFromHub: true,
    actions: [{ id: "sat-xml", label: "Descargar acuse de declaración" }],
  },
  {
    id: TRAMITES_TAB_IDS.VALIDACION_RFC,
    label: "Validación RFC",
    title: "Validación de RFC SAT",
    description: "Validación individual o masiva de RFC/CURP contra el portal del SAT.",
    iconKey: "validate",
    navSection: "SAT",
    /** Oculto en hub/sidebar: pantalla canónica en Datos Maestros */
    hideFromHub: true,
    embedded: TRAMITES_TAB_IDS.VALIDACION_RFC,
  },
  {
    id: TRAMITES_TAB_IDS.INSCRIPCION_SAT,
    label: "Inscripción SAT",
    title: "Inscripción de trabajadores SAT",
    description: "Generación de archivos y cartas para alta en el portal del SAT.",
    iconKey: "userplus",
    navSection: "SAT",
    embedded: TRAMITES_TAB_IDS.INSCRIPCION_SAT,
  },
  {
    id: TRAMITES_TAB_IDS.VALIDADOR_NSS,
    label: "Validador NSS",
    title: "Validador NSS (IMSS / IDSE)",
    description: "Consulta y confirmación de NSS mediante IDSE.",
    iconKey: "validate",
    navSection: "IMSS",
    embedded: TRAMITES_TAB_IDS.VALIDADOR_NSS,
  },
  {
    id: TRAMITES_TAB_IDS.INSCRIPCION_IMSS,
    label: "Inscripción IMSS",
    title: "Inscripción de trabajadores IMSS",
    description: "Alta patronal, NSS, SDI y movimientos afiliatorios.",
    iconKey: "users",
    navSection: "IMSS",
    embedded: TRAMITES_TAB_IDS.INSCRIPCION_IMSS,
  },
  {
    id: TRAMITES_TAB_IDS.DESCARGAS_IMSS_INFONAVIT,
    label: "Descargas IMSS/INFONAVIT",
    title: "Descargas IMSS/INFONAVIT",
    description:
      "Movimientos afiliatorios, cuotas obrero-patronales, informes IDSE, aportaciones patronales, créditos y cumplimiento ante el INFONAVIT.",
    iconKey: "users",
    navSection: "IMSS / INFONAVIT",
    actions: [
      { id: "imss-mov", label: "Descargar movimientos afiliatorios", group: "IMSS" },
      { id: "imss-cuotas", label: "Descargar resumen de cuotas del periodo", group: "IMSS" },
      { id: "imss-idse", label: "Descargar acuse IDSE", group: "IMSS" },
      { id: "inf-aport", label: "Descargar aportaciones patronales", group: "INFONAVIT" },
      { id: "inf-credito", label: "Descargar estado de crédito por NSS", group: "INFONAVIT" },
      { id: "inf-bimestre", label: "Descargar acuse bimestral", group: "INFONAVIT" },
    ],
  },
];

function isTramitesTabVisibleInHub(tab) {
  return !tab?.hideFromHub;
}

const TRAMITES_DEDICATED_TAB_PATHS = {
  [TRAMITES_TAB_IDS.SAT]: FISCAL_DOCS_OWNER.route,
  [TRAMITES_TAB_IDS.VALIDACION_RFC]: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  [TRAMITES_TAB_IDS.INSCRIPCION_SAT]: "/tramites/inscripcion-trabajadores-sat",
  [TRAMITES_TAB_IDS.INSCRIPCION_IMSS]: "/tramites/inscripcion-trabajadores-imss",
  [TRAMITES_TAB_IDS.VALIDADOR_NSS]: TRAMITES_VALIDADOR_NSS_PATH,
  [TRAMITES_TAB_IDS.MATERIALIDAD]: "/materialidad/expediente",
};

/** URL de navegación por pestaña (rutas propias del módulo Trámites). */
export function resolveTramitesTabNavPath(tabId, buildUrl) {
  if (TRAMITES_DEDICATED_TAB_PATHS[tabId]) return TRAMITES_DEDICATED_TAB_PATHS[tabId];
  const toUrl = buildUrl || ((id) => {
    if (!id || id === TRAMITES_TAB_IDS.SAT) return FISCAL_DOCS_OWNER.route;
    return `/tramites-fiscales?tab=${encodeURIComponent(id)}`;
  });
  return toUrl(tabId);
}

/** Tarjetas del hub (/tramites/inicio) derivadas del manifiesto. */
export function getTramitesFeatureCards(buildUrl) {
  return TRAMITES_TAB_CONFIG.filter(isTramitesTabVisibleInHub).map((tab) => ({
    path: resolveTramitesTabNavPath(tab.id, buildUrl),
    label: tab.id === TRAMITES_TAB_IDS.VALIDADOR_NSS ? tab.title : tab.title || tab.label,
    description: tab.description,
    iconKey: tab.iconKey,
    testid: `tramites-hub-${tab.id}`,
  }));
}

/** Ítems del sidebar (modo híbrido: hub + acceso directo por función). */
export function getTramitesSidebarNavItems(buildUrl) {
  return TRAMITES_TAB_CONFIG.filter(isTramitesTabVisibleInHub).map((tab) => ({
    to: resolveTramitesTabNavPath(tab.id, buildUrl),
    label: tab.id === TRAMITES_TAB_IDS.VALIDADOR_NSS ? tab.title : tab.label,
    iconKey: tab.iconKey,
    navSection: tab.navSection,
    testid: `nav-tramites-${tab.id}`,
    tramitesTabId: tab.id,
  }));
}
